import { CallEmsApi, iEmsApiResponseBody } from "./FetchExtension";

/*
 * The body of POST /Contacts (CreateContactRequest in the API).
 * The flags are booleans here; the API stores them in tblContacts as "T" / "F".
 */
export interface CreateContactRequest {
    name: string;
    emailAddress: string;
    sourceID: number;
    priority: number; // 1 = highest, 255 = lowest
    autoRespond: boolean;
    htmlEnabled: boolean;
    spam: boolean;
}

export interface CreateContactResponse {
    id: number;
}

// PUT /Contacts/{id} takes the same fields as a create
export type UpdateContactRequest = CreateContactRequest;

/*
 * One row of GET /Contacts. Contacts the legacy system created can have no name,
 * source or priority, so those may be null.
 */
export interface ContactDTO {
    id: number;
    name: string | null;
    emailAddress: string;
    sourceID: number | null;
    sourceName: string | null;
    priority: number | null;
    autoRespond: boolean;
    htmlEnabled: boolean;
    spam: boolean;
    updatedAt?: string | null;
}

// a mapping sends new enquiries from the contact in that mailbox to the user
export interface ContactMappingDTO {
    mailboxID: number;
    mailboxName: string | null;
    mailboxEmailAddress: string | null;
    user: { id: number; firstname: string | null; lastname: string | null } | null;
}

// GET /Contacts/{id}: the contact with its mailbox mappings
export interface GetContactResponse extends ContactDTO {
    mappings: ContactMappingDTO[];
}

export interface AddContactMappingRequest {
    mailboxID: number;
    userID: number;
}

export interface GetAllContactsResponse {
    items: ContactDTO[];
}

export interface SourceDTO {
    id: number;
    name: string;
    isSystem: boolean;
}

export interface GetSourcesResponse {
    items: SourceDTO[];
}

// Source 0 is "[All]", a filter value rather than a real source, so a contact cannot use it.
export const ALL_SOURCES_ID = 0;

export async function CreateContact(
    request: CreateContactRequest,
): Promise<iEmsApiResponseBody<CreateContactResponse>> {
    return await CallEmsApi<CreateContactResponse>("POST", "Contacts", request);
}

export async function UpdateContact(id: number, request: UpdateContactRequest): Promise<iEmsApiResponseBody<void>> {
    return await CallEmsApi<void>("PUT", `Contacts/${id}`, request);
}

export async function DeleteContact(id: number): Promise<iEmsApiResponseBody<void>> {
    return await CallEmsApi<void>("DELETE", `Contacts/${id}`);
}

export async function GetAllContacts():Promise<iEmsApiResponseBody<GetAllContactsResponse>> {
    return await CallEmsApi<GetAllContactsResponse>("GET", "Contacts");
}

export async function GetContact(id: number): Promise<iEmsApiResponseBody<GetContactResponse>> {
    return await CallEmsApi<GetContactResponse>("GET", `Contacts/${id}`);
}

export async function AddContactMapping(
    contactID: number,
    request: AddContactMappingRequest,
): Promise<iEmsApiResponseBody<void>> {
    return await CallEmsApi<void>("POST", `Contacts/${contactID}/Mappings`, request);
}

export async function RemoveContactMapping(contactID: number, mailboxID: number): Promise<iEmsApiResponseBody<void>> {
    return await CallEmsApi<void>("DELETE", `Contacts/${contactID}/Mappings/${mailboxID}`);
}

export async function GetSources(): Promise<iEmsApiResponseBody<GetSourcesResponse>> {
    return await CallEmsApi<GetSourcesResponse>("GET", "Sources");
}
